import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments, faPaperPlane, faTimes, faRobot } from '@fortawesome/free-solid-svg-icons';
import { API_BASE } from './apiConfig'; 

const ChatBot = () => {
    const { t, i18n } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const scrollRef = useRef(null);

    const isMobile = window.innerWidth <= 768;

    // Թարմացնում ենք ողջույնի հաղորդագրությունը լեզուն փոխելիս
    useEffect(() => {
        setMessages([
            { text: t('bot_welcome'), sender: 'bot' }
        ]);
    }, [i18n.language, t]);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, loading, isOpen]);

    const handleSend = async (e) => {
        if (e) e.preventDefault();
        if (!input.trim() || loading) return;

        const userMsg = input.trim();
        setMessages(prev => [...prev, { text: userMsg, sender: 'user' }]);
        setInput('');
        setLoading(true);

        try {
            const res = await fetch(`${API_BASE}/api/bot-system/bot/ask`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                   
                },
                body: JSON.stringify({ 
                    question: userMsg, 
                    userId: localStorage.getItem('userId') || 'guest',
                    lang: i18n.language 
                })
            });

            if (!res.ok) throw new Error(`Server Error: ${res.status}`);

            const data = await res.json();
            setMessages(prev => [...prev, { 
                text: data.answer || t('bot_error_response'), 
                sender: 'bot' 
            }]);

        } catch (error) {
            console.error("Bot API Error:", error);
            setMessages(prev => [...prev, { 
                text: t('bot_connection_error'), 
                sender: 'bot' 
            }]);
        } finally {
            setLoading(false);
        }
    };

    // --- Dynamic Inline Styles ---
    const wrapperStyle = { 
        position: 'fixed', 
        bottom: isMobile ? '110px' : '25px', 
        right: '25px', 
        zIndex: 2000000, 
        fontFamily: "'Sora', sans-serif",
        transition: 'all 0.3s ease'
    };

    const windowStyle = { 
        position: isMobile ? 'fixed' : 'absolute',
        bottom: isMobile ? '0' : '75px',
        right: isMobile ? '0' : '0',
        top: isMobile ? '0' : 'auto',
        left: isMobile ? '0' : 'auto',
        width: isMobile ? '100vw' : '320px', 
        height: isMobile ? '100vh' : '450px', 
        background: '#fff', 
        borderRadius: isMobile ? '0' : '20px', 
        boxShadow: '0 15px 45px rgba(0,0,0,0.2)', 
        display: 'flex', 
        flexDirection: 'column', 
        overflow: 'hidden',
        border: isMobile ? 'none' : '1px solid rgba(197, 160, 89, 0.1)',
        animation: 'slideUp 0.3s ease-out',
        zIndex: 2000001
    };

    const headerStyle = { 
        background: '#0f172a', 
        color: '#fff', 
        padding: isMobile ? '50px 20px 15px' : '15px 20px', 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '2px solid #c5a059'
    };

    const msgAreaStyle = { 
        flex: 1, 
        padding: '15px', 
        overflowY: 'auto', 
        background: '#fcfcfc',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        paddingBottom: isMobile ? '30px' : '15px'
    };

    return (
        <div style={wrapperStyle}>
            {isOpen && (
                <div style={windowStyle}>
                    <div style={headerStyle}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                            <div style={botAvatarHeader}>
                                <FontAwesomeIcon icon={faRobot} />
                            </div>
                            <div>
                                <div style={{fontSize: '15px', fontWeight: '800', color: '#c5a059'}}>Liber Assistant</div>
                                <div style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                                    <div style={{width: '7px', height: '7px', background: '#22c55e', borderRadius: '50%'}}></div>
                                    <span style={{fontSize: '11px', opacity: 0.8}}>{t('bot_online')}</span>
                                </div>
                            </div>
                        </div>
                        <div 
                            onClick={() => setIsOpen(false)} 
                            style={{ padding: '10px', cursor: 'pointer' }}
                        >
                            <FontAwesomeIcon icon={faTimes} style={{ fontSize: '22px', color: '#fff' }} />
                        </div>
                    </div>
                    
                    <div style={msgAreaStyle} ref={scrollRef}>
                        {messages.map((m, i) => (
                            <div key={i} style={m.sender === 'user' ? userRow : botRow}>
                                <div style={m.sender === 'user' ? userBubble : botBubble}>{m.text}</div>
                            </div>
                        ))}
                        {loading && (
                            <div style={botRow}>
                                <div style={botBubble}>
                                    <div className="typing-dots">
                                        <span>.</span><span>.</span><span>.</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <form onSubmit={handleSend} style={formStyle}>
                        <input 
                            value={input} 
                            onChange={(e) => setInput(e.target.value)} 
                            placeholder={t('bot_placeholder')} 
                            style={inputStyle} 
                        />
                        <button 
                            type="submit" 
                            style={{...sendBtnStyle, opacity: input.trim() ? 1 : 0.5}} 
                            disabled={!input.trim() || loading}
                        >
                            <FontAwesomeIcon icon={faPaperPlane} />
                        </button>
                    </form>
                </div>
            )}

            {!isOpen && (
                <div 
                    style={{
                        ...toggleBtnStyle, 
                        background: '#c5a059'
                    }} 
                    onClick={() => setIsOpen(true)}
                >
                    <FontAwesomeIcon icon={faComments} size="lg" />
                </div>
            )}
 

            <style>{`
                @keyframes slideUp {
                    from { transform: translateY(100%); opacity: 0; }
                    to { transform: translateY(0); opacity: 1; }
                }
                .typing-dots span {
                    animation: blink 1.4s infinite;
                    font-size: 24px;
                    display: inline-block;
                    line-height: 10px;
                    color: #c5a059;
                }
                .typing-dots span:nth-child(2) { animation-delay: 0.2s; }
                .typing-dots span:nth-child(3) { animation-delay: 0.4s; }
                @keyframes blink {
                    0% { opacity: 0.1; }
                    20% { opacity: 1; }
                    100% { opacity: 0.1; }
                }
            `}</style>
        </div>
    );
};

// --- Static Styles ---

const toggleBtnStyle = { 
    width: '60px', 
    height: '60px', 
    borderRadius: '50%', 
    color: '#fff', 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    cursor: 'pointer', 
    boxShadow: '0 8px 25px rgba(197, 160, 89, 0.3)',
    transition: 'all 0.3s ease'
};

const botAvatarHeader = {
    width: '38px',
    height: '38px',
    background: 'rgba(197, 160, 89, 0.2)',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    color: '#c5a059'
};

const formStyle = { 
    display: 'flex', 
    padding: '15px 20px', 
    borderTop: '1px solid #f1f5f9', 
    background: '#fff',
    alignItems: 'center',
    paddingBottom: window.innerWidth <= 768 ? '35px' : '15px' // Keyboard fix
};

const inputStyle = { 
    flex: 1, 
    border: 'none', 
    outline: 'none', 
    fontSize: '16px',
    padding: '10px'
};

const sendBtnStyle = { 
    background: '#c5a059', 
    border: 'none', 
    color: '#fff', 
    width: '45px', 
    height: '45px', 
    borderRadius: '12px', 
    cursor: 'pointer',
    transition: '0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
};

const botRow = { display: 'flex', justifyContent: 'flex-start' };
const userRow = { display: 'flex', justifyContent: 'flex-end' };

const botBubble = { 
    background: '#fff', 
    color: '#1e293b',
    padding: '14px 18px', 
    borderRadius: '18px 18px 18px 5px', 
    fontSize: '14px',
    boxShadow: '0 3px 10px rgba(0,0,0,0.03)',
    border: '1px solid #f1f5f9',
    maxWidth: '85%',
    lineHeight: '1.5'
};

const userBubble = { 
    background: '#c5a059', 
    color: '#fff', 
    padding: '14px 18px', 
    borderRadius: '18px 18px 5px 18px', 
    fontSize: '14px',
    maxWidth: '85%',
    boxShadow: '0 4px 12px rgba(197, 160, 89, 0.2)',
    lineHeight: '1.5',
};

export default ChatBot;